// frontend/src/pages/reports/ExcessStockReport.jsx
// Report 3: Projected Stock
//
// 3-month cascading projection (M1/M2/M3) per item-location.
// M1 forecast is netted against actual MTD sales.
// Incoming POs are time-phased by NeedByDate month.

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { Button, Tooltip } from '@mui/material';
import PageBanner from '../../components/common/PageBanner';
import Tile from '../../components/layout/Tile';
import ReportKpiStrip from '../../components/reports/ReportKpiStrip';
import PartialPriceChip from '../../components/reports/PartialPriceChip';
import StatusChip from '../../components/common/StatusChip';
import { ItemFilterBar, useItemFilter } from '../../features/item-filter';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import { useIsShopify, itemIdentityColumns } from '../../components/grid/productIdentity';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useProductGrouping, VARIANT_ROW_SELECTION } from '../../components/grid/productGrouping';
import { useReportFilters } from '../../hooks/useReportFilters';
import { useCreatePromotionFromExcess } from '../../hooks/useReportActionMutations';
import { translateRequestError } from '../../queries/api';
import { showToast } from '../../components/common/appToastBus';
import { usePromotionResult } from './usePromotionResult';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, copyOnlyContextMenuItems, restoreInitialSort } from '../../utils/gridDefaults';
import { scopeHeaderCheckboxToPage } from '../../utils/gridSelection';
import GridExportButton from '../../components/common/GridExportButton';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../../utils/pinnedTotals';

import '../../styles/pages.css';
import './ExcessStockReport.css';
import { GridEmptyState } from '../../components/grid/NoRowsOverlay';
import { GridPage, GridViewport } from '../../components/grid/GridPage';


function NpiChip({ value }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <Tooltip title={t('common.tooltips.npi')} describeChild>
      <StatusChip label={t('reports.excess.columns.npi')} color="info" />
    </Tooltip>
  );
}


function ExcessStockReport() {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatCompact, formatMonthYear } = useFormatters();
  const defaultHidden = useMemo(() => ['shopify_product_title'], []);
  // The month groups run in time order, each month's columns under it.
  // Group variants by product (productGrouping): the switch, the product
  // tree, the row id and the saved column state, one preference for every
  // report. A product row carries the report's totals restricted to that
  // product and the report's own measure over it; only those columns say
  // `sortable: true`, since the server orders the products by them.
  const grouping = useProductGrouping({
    gridId: 'report_excess_stock',
    preferenceKey: 'reports_grouped',
    defaultHidden,
    leafIdFields: ['item_id', 'location_id'],
    movableColumns: false,
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridRef = useRef(null);

  const itemFilter = useItemFilter();
  const { location, setLocation, productCategory, setProductCategory } = useReportFilters('excess_stock');

  // KPI state declared before `params` so the memo can reference it.
  const [kpiFilter, setKpiFilter] = useState(null);

  // KPI cards drive the backend `kpi_filter` for `excess` and `high_cover`
  // (row-narrowing behavior); `high_value` is sort-only on the frontend so
  // we don't pass it as a filter — backend `data` ignores it.
  const params = useMemo(() => ({
    location_code: location, product_category_id: productCategory?.id || null, ...itemFilter.params,
    kpi_filter: (kpiFilter === 'excess' || kpiFilter === 'high_cover') ? kpiFilter : undefined,
  }), [location, productCategory, itemFilter.params, kpiFilter]);
  const [chrome, setChrome] = useState({
    summary: null, currency: null, totals: null, months: null,
  });
  const onData = useCallback((data) => {
    setChrome({
      summary: data.summary ?? null,
      currency: data.currency ?? null,
      totals: data.totals ?? null,
      // `months` (M1/M2/M3 column header labels) rides along on every page
      // response — kept in chrome so we don't fire a second request on mount.
      months: data.months ?? null,
    });
  }, []);
  const { onGridReady, onGridPreDestroyed, loading, error, pagination } = useSsrmGrid({
    endpoint: '/api/reports/excess-items/data/',
    params,
    onData,
    gridRef,
  });
  const { summary, currency, totals } = chrome;
  const reportMonths = chrome.months;

  const showPromotionResult = usePromotionResult('excess');

  // Server-side export of the full filtered dataset (respects KPI filter).
  const exportProps = {
    endpoint: '/api/reports/excess-items/export/',
    filename: 'excess_stock',
    getParams: useCallback(
      (scope) => (scope === 'all' ? '' : buildExportQuery(params)),
      [params],
    ),
  };

  const data = useMemo(
    () => ({ summary, currency, months: reportMonths }),
    [summary, currency, reportMonths],
  );

  // Row selection for "Create Promotion"
  const [selectedCount, setSelectedCount] = useState(0);
  const createPromotionMutation = useCreatePromotionFromExcess();
  const creating = createPromotionMutation.isPending;

  const handleSelectionChanged = useCallback((e) => {
    // The header checkbox produces an SSRM select-all RULE, under which
    // getSelectedRows() returns [] and this button would never appear.
    // create-promotion takes a materialised item list, so the selection can
    // only cover rows the grid has loaded — confine it to the current page.
    // Covering every filtered row would mean teaching that endpoint to accept
    // a selection rule the way /api/suppliers/bulk-delete/ does.
    scopeHeaderCheckboxToPage(e.api);
    setSelectedCount(e.api.getSelectedRows().length);
  }, []);

  const handleCreatePromotion = useCallback(async () => {
    const api = gridRef.current;
    if (!api) return;

    const selectedRows = api.getSelectedRows();
    if (!selectedRows.length) return;

    const items = selectedRows.map(r => ({
      item_id: r.item_id,
      location_id: r.location_id,
      item_code: r.item_code,
    }));

    try {
      const result = await createPromotionMutation.mutateAsync({ items });
      api.deselectAll();
      showPromotionResult(result);
    } catch (err) {
      console.error('createPromotionFromExcess:', err.message);
      showToast(translateRequestError(err, t, 'reports.excess.actions.createPromoFailed'), 'error');
    }
  }, [createPromotionMutation, t, showPromotionResult]);

  const handleKpiClick = useCallback((key) => {
    const newKey = kpiFilter === key ? null : key;
    setKpiFilter(newKey);

    const api = gridRef.current;
    if (!api) return;
    if (newKey === 'high_value') {
      api.applyColumnState({ state: [{ colId: 'excess_value', sort: 'desc' }], defaultState: { sort: null } });
    } else if (newKey === 'high_cover') {
      api.applyColumnState({ state: [{ colId: 'days_of_cover', sort: 'desc' }], defaultState: { sort: null } });
    } else {
      restoreInitialSort(api);
    }
  }, [kpiFilter]);

  const kpiItems = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    const ccy = data.currency;
    // Null when items hold excess and not one of them can be priced — an
    // unknown value, which the tile must not print as a currency-formatted 0
    // (`parseFloat(null || 0)` did exactly that). The chip beside the grid
    // names the remedy.
    const formattedValue = s.total_excess_value == null
      ? '—'
      : formatCompact(parseFloat(s.total_excess_value), ccy);
    return [
      { label: t('reports.excess.kpi.totalItems'), value: s.total_items, key: 'all' },
      { label: t('reports.excess.kpi.excessItems'), value: s.excess_items, key: 'excess', tooltip: t('reports.excess.kpi.excessItemsTooltip') },
      { label: t('reports.excess.kpi.totalExcessValue'), value: formattedValue, key: 'high_value', tooltip: t('reports.excess.kpi.totalExcessValueTooltip') },
      { label: t('reports.excess.kpi.avgDaysOfCover'), value: s.avg_days_of_cover, suffix: ` ${t('common.days')}`, key: 'high_cover', tooltip: t('reports.excess.kpi.avgDaysOfCoverTooltip') },
    ];
  }, [data, t, formatCompact]);

  // Pinned totals: server provides `totals` over the FULL filtered dataset.
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  // Month labels from API response
  const monthLabels = useMemo(() => {
    if (!data?.months) return ['M1', 'M2', 'M3'];
    return data.months.map(iso => formatMonthYear(iso));
  }, [data?.months, formatMonthYear]);

  const currencySuffix = currency ? ` (${currency})` : '';
  const decimalFormatter = useCallback(
    (p) => p.value != null ? formatNumber(p.value, 2) : '—',
    [formatNumber]
  );

  const projectedCellClassRules = useMemo(() => ({
    'ag-cell-attention-error': (p) => p.value != null && p.value < 0,
  }), []);

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    // -- Identity --
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('reports.columns.product'),
      codeHeader: t('reports.columns.itemCode'),
      nameHeader: t('reports.columns.itemName'),
    })),
    { field: 'category_label', headerName: t('reports.columns.productCategory'), minWidth: 100 },
    { field: 'location_name', headerName: t('reports.columns.location'), minWidth: 100 },
    {
      field: 'npi_flag', headerName: t('reports.excess.columns.npi'), type: 'centeredColumn',
      headerTooltip: t('common.tooltips.npi'),
      cellRenderer: NpiChip,
    },
    { field: 'abc_class', headerName: t('reports.columns.abcClass'), width: 60, type: 'centeredColumn' },

    // -- M1 (current month — includes stock position as starting point) --
    {
      headerName: monthLabels[0],
      children: [
        { field: 'on_hand', sortable: true, headerName: t('reports.excess.columns.onHand'), type: 'numberColumn' },
        { field: 'committed', sortable: true, headerName: t('reports.excess.columns.committed'), type: 'numberColumn' },
        { field: 'available', sortable: true, headerName: t('reports.excess.columns.available'), type: 'numberColumn' },
        { field: 'm1_forecast', sortable: true, headerName: t('reports.excess.columns.forecast'), type: 'numberColumn' },
        { field: 'm1_actual', headerName: t('reports.excess.columns.actualSales'), type: 'numberColumn' },
        { field: 'm1_netted_forecast', headerName: t('reports.excess.columns.nettedForecast'), type: 'numberColumn', headerTooltip: t('reports.excess.tooltips.nettedForecast') },
        { field: 'm1_incoming', sortable: true, headerName: t('reports.excess.columns.inTransit'), type: 'numberColumn' },
        { field: 'm1_projected', sortable: true, headerName: t('reports.excess.columns.projected'), type: 'numberColumn', headerTooltip: t('reports.excess.tooltips.projected'), cellClassRules: projectedCellClassRules },
      ],
    },

    // Each month after the first, and the summary, starts with the line of
    // styles/pages.css `.ag-column-group-start`: on the group header, the
    // first column's header and its cells.
    // -- M2 (tinted) --
    {
      headerName: monthLabels[1],
      headerClass: 'excess-group-tinted ag-column-group-start',
      children: [
        { field: 'm2_forecast', sortable: true, headerName: t('reports.excess.columns.forecast'), type: 'numberColumn', headerClass: 'ag-column-group-start', cellClass: 'excess-col-tinted ag-column-group-start' },
        { field: 'm2_incoming', sortable: true, headerName: t('reports.excess.columns.inTransit'), type: 'numberColumn', cellClass: 'excess-col-tinted' },
        { field: 'm2_projected', sortable: true, headerName: t('reports.excess.columns.projected'), type: 'numberColumn', headerTooltip: t('reports.excess.tooltips.projected'), cellClassRules: projectedCellClassRules, cellClass: 'excess-col-tinted' },
      ],
    },

    // -- M3 --
    {
      headerName: monthLabels[2],
      headerClass: 'ag-column-group-start',
      children: [
        { field: 'm3_forecast', sortable: true, headerName: t('reports.excess.columns.forecast'), type: 'numberColumn', headerClass: 'ag-column-group-start', cellClass: 'ag-column-group-start' },
        { field: 'm3_incoming', sortable: true, headerName: t('reports.excess.columns.inTransit'), type: 'numberColumn' },
        { field: 'm3_projected', sortable: true, headerName: t('reports.excess.columns.projected'), type: 'numberColumn', headerTooltip: t('reports.excess.tooltips.projected'), cellClassRules: projectedCellClassRules },
      ],
    },

    // -- Summary (tinted) --
    {
      headerName: t('reports.excess.groups.summary'),
      headerClass: 'excess-group-tinted ag-column-group-start',
      children: [
        {
          field: 'daily_demand', headerName: t('reports.excess.columns.dailyDemand'), type: 'decimalColumn', valueFormatter: decimalFormatter,
          headerTooltip: t('common.tooltips.dailyDemand'), headerClass: 'ag-column-group-start', cellClass: 'excess-col-tinted ag-column-group-start',
        },
        {
          field: 'days_of_cover', headerName: t('reports.excess.columns.daysOfCover'), type: 'numberColumn',
          headerTooltip: t('common.tooltips.daysOfCover'),
          valueFormatter: (p) => p.value == null ? t('reports.excess.noDemand') : formatNumber(p.value, 0),
          cellClass: 'excess-col-tinted',
        },
        { field: 'target_stock', headerName: t('reports.excess.columns.targetStock'), type: 'numberColumn', headerTooltip: t('common.tooltips.targetStock'), cellClass: 'excess-col-tinted' },
        { field: 'excess_qty', sortable: true, headerName: t('reports.excess.columns.excessQty'), type: 'numberColumn', headerTooltip: t('reports.excess.tooltips.excessQty'), cellClass: 'excess-col-tinted' },
        { field: 'unit_cost', headerName: t('reports.excess.columns.costPrice') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter, cellClass: 'excess-col-tinted' },
        { field: 'excess_value', sortable: true, initialSort: 'desc', headerName: t('reports.excess.columns.excessValue') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter, cellClass: 'excess-col-tinted' },
      ],
    },
  ]), [isShopify, isGrouped, groupedColumns, variantProductColumn, t, formatNumber, monthLabels, currencySuffix, decimalFormatter, projectedCellClassRules]);

  return (
    <div className="page-container">
      <ReportKpiStrip title={t('reports.excess.kpi.title')} items={kpiItems} loading={loading} activeKey={kpiFilter} onCardClick={handleKpiClick} storageKey="excess_stock" />

      <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'common.fetchError')]} />

      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.reportExcess')}
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            <div className="report-caveats">
              <PartialPriceChip
                gap="cost"
                count={summary?.uncosted_excess_items}
                disclose={summary?.partial_cost}
              />
            </div>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: productCategory, set: setProductCategory, show: true }}
              locationFilter={{ selected: location, set: setLocation }}
              actions={<>
                {grouping.toggle}
                {selectedCount > 0 ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreatePromotion}
                    loading={creating}
                  >
                    {t('reports.excess.actions.createPromotion', { count: selectedCount })}
                  </Button>
                ) : null}
              </>}
            />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={location !== 'all' || !!productCategory || !!itemFilter.filter || !!kpiFilter}
                >
                  <AgGridReact
                    theme={gridTheme} columnDefs={columnDefs} columnTypes={columnTypes}
                    loading={loading}
                    {...defaultGridProps} defaultColDef={gridDefaultColDef}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    rowSelection={VARIANT_ROW_SELECTION}
                    onSelectionChanged={handleSelectionChanged}
                    {...grouping.gridProps}
                    rowModelType="serverSide"
                    {...pagination}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default ExcessStockReport;
